import {useEffect, useMemo, useRef, useState} from "react";
import { ROUTES } from "../../../constants";
import BoxLoader from "../../components/general/boxLoader";
import {AsyncSelectField} from "../../components/general/form/selectField";
import TextField from "../../components/general/form/textField";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import { useGetDoctors } from "../../../hook/request/doctors";
import { TAddScheduleFields } from "../../../types/schedule";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import TextAreaField from "../../components/general/form/textAreaField";
import DatePickerField from "../../components/general/form/datePickerField";
import { addScheduleSchema, editScheduleSchema } from "../../../schema/scheduleSchema";
import { useAddSchedule, useGetSchedule } from "../../../hook/request/schedule";
import { getStartOfTodayAsString, mFormat} from "../../../utils/date";
import {useMySearchParams} from "../../../hook/useMySearchParams";
import momentJ from 'moment-jalaali';
import {asyncSelectLoadOptions} from "../../../utils/asyncSelectLoadOptions";
import {localStorageGet} from "../../../utils/localStorage";
import {useFindAccessInAccessList} from "../../../constants/constant/accessProcess";
import {useSelector} from "react-redux";

type TFilterValues = {
  pageNumber?: string;
  perPage?: string;
  search?: string;
}

const AddSchedule = () => {

  const navigate = useNavigate();
  const isProvider = useSelector((state: any) => state.general.userDataReducer.isProvider);
  const {findAccessInAccessList} = useFindAccessInAccessList();
  const { id } = useParams();
  const { getQueryParams } = useMySearchParams();
  const [searchParams] = useSearchParams();
  const [doctorFilterValues, setDoctorFilterValues] = useState<TFilterValues>({
    pageNumber: "0",
    perPage: "10",
    search: "",
  });
  const [fieldValue, setFieldValue] = useState<TAddScheduleFields>({
    provider: '',
    name: '',
    fromDate: null,
    toDate: null,
    description: '',
  });
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TAddScheduleFields>({
    defaultValues: useMemo(() => fieldValue, [fieldValue]),
    resolver: yupResolver(id ? editScheduleSchema : addScheduleSchema),
  });
  const [schedulesList, setSchedulesList] = useState([]);
  const [isOperator, setIsOperator] = useState<boolean>(false);
  const [fetch, setFetch] = useState<boolean>(true)

  const callbackRef = useRef<any>(null);

  // services
  const getDoctorsRequest = useGetDoctors(getQueryParams(doctorFilterValues), (isOperator && fetch));
  const getScheduleRequest = useGetSchedule();
  const addScheduleRequest = useAddSchedule();

  useEffect(() => {
    const _userData = localStorageGet('USER_DATA')
    if (_userData?.operatorId !== 0) {
      setIsOperator(true);
    } else {
      setIsOperator(false);
      setFieldValue({ ...fieldValue, provider: '0' });
    }
  }, []);


  useEffect(() => {
    let _DoctorFilterValues = {};
    _DoctorFilterValues = {
      ...doctorFilterValues,
      pageNumber: searchParams.get("pageNumber") || "0",
      perPage: searchParams.get("perPage") || "10",
      search: searchParams.get("search") || ""
    }
    setDoctorFilterValues(_DoctorFilterValues)
    setFetch(false)
  }, [searchParams]);

  useEffect(() => {
    id && getScheduleRequest.mutate({id});
  }, [id])

  useEffect(() => {
    reset(fieldValue);
  }, [fieldValue]);

  useEffect(() => {
    if(getDoctorsRequest?.data?.data?.result) {
      const { data } = getDoctorsRequest.data.data.result,
        _data = data.map((item: any) => (
          { label: `${item?.firstName} ${item?.lastName}`, value: item?.id }
        ))
      setSchedulesList(_data);
    }
  }, [getDoctorsRequest.data])

  useEffect(() => {
    if (getScheduleRequest?.data?.data?.result) {
      const { provider, name, fromDate, toDate, description } = getScheduleRequest.data.data.result,
        _fieldValue = {
          provider: provider?.id, name, description,
          fromDate: fromDate ? momentJ(fromDate) : null,
          toDate: toDate ? momentJ(toDate) : null,
        };
      setFieldValue(_fieldValue);
    }
  }, [getScheduleRequest.data])

  const changeRequestFilters = (inputValue: any, callback: any) => {
    callbackRef.current = callback;
    setDoctorFilterValues({
      ...doctorFilterValues,
      search: inputValue,
    })
  }

  useEffect(() => {
    if(callbackRef.current) {
      doctorsLoadOptions(doctorFilterValues.search, callbackRef.current);
    }
  }, [doctorFilterValues?.search]);

  const doctorsLoadOptions = (inputValue: any, callback: any) => {
    asyncSelectLoadOptions(inputValue, callback, doctorFilterValues, setDoctorFilterValues, getDoctorsRequest,
      (item) => ({ label: `${item?.firstName} ${item?.lastName}`, value: item?.id })
    );
  };

  const addScheduleSubmit: SubmitHandler<TAddScheduleFields> = (data) => {
    const { provider, name, fromDate, toDate, description } = data,
      body = {
        ...(id ? {id} : {}),
        name,
        fromDate: mFormat(fromDate),
        toDate: mFormat(toDate),
        provider: {
          id: isOperator ? provider && +provider : 0,
          name: ""
        },
        description,
      };
    addScheduleRequest.mutate(body);
  }

  return (
    <div className="card-box mb-3">
      {
        (
          getDoctorsRequest?.isLoading ||
          getScheduleRequest?.isPending ||
          addScheduleRequest?.isPending
        ) && <BoxLoader type="cover" />
      }
      <div className="form-box w-800 mw-100 mx-auto py-3">
        <form onSubmit={handleSubmit(addScheduleSubmit)}>
          <div className="row">
            <div className="col-12 px-4">
              <h4 className="title-dot font-16 font-weight-bold mb-5">اطلاعات زمان‌بندی</h4>
            </div>
            {isOperator &&
              <div className="col-12 col-md-6 px-4">
                <div className="form-group mb-4">
                  <label className="inp-lbl me-2 mb-2" htmlFor="provider">نام پزشک</label>
                  <AsyncSelectField
                    options={schedulesList}
                    name="provider"
                    placeholder='انتخاب کنید'
                    control={control}
                    error={errors}
                    asyncLoadOptions={changeRequestFilters}
                    isDisabled={!!id}
                  />
                </div>
              </div>
            }
            <div className="col-12 col-md-6 px-4">
              <div className="form-group mb-4">
                <label className="inp-lbl me-2 mb-2" htmlFor="name">نام برنامۀ زمان‌بندی</label>
                <TextField
                  name="name"
                  placeholder="نام برنامۀ زمان‌بندی"
                  control={control}
                  error={errors}
                />
              </div>
            </div>
            <div className="col-12 col-md-6 px-4">
              <div className="form-group mb-4">
                <label className="inp-lbl me-2 mb-2" htmlFor="fromDate">تاریخ شروع برنامه</label>
                <DatePickerField
                  className={'text-black'}
                  name="fromDate"
                  placeholder="تاریخ شروع برنامه"
                  control={control}
                  error={errors}
                  minDate={getStartOfTodayAsString()}
                  disabled={!!id}
                />
              </div>
            </div>
            <div className="col-12 col-md-6 px-4">
              <div className="form-group mb-4">
                <label className="inp-lbl me-2 mb-2" htmlFor="toDate">تاریخ پایان برنامه</label>
                <DatePickerField
                  name="toDate"
                  placeholder="تاریخ پایان برنامه"
                  control={control}
                  error={errors}
                  minDate={getStartOfTodayAsString()}
                />
              </div>
            </div>
            <div className="col-12 px-4">
              <div className="form-group mb-5">
                <label className="inp-lbl me-2 mb-2" htmlFor="description">‌توضیحات</label>
                <TextAreaField
                  name="description"
                  placeholder="‌توضیحات"
                  control={control}
                  error={errors}
                />
              </div>
            </div>
            <div className="col-12 px-4 d-flex justify-content-end">
              <div className="d-flex form-group mb-5 mr-auto mt-auto pb-1">
                <button
                  type='button'
                  onClick={() => navigate(ROUTES?.SCHEDULE?.PATH)}
                  className="btn btn-outline-gray rounded d-flex align-items-center px-4 p-2"
                >
                  بازگشت
                </button>
                {
                  (isProvider || (findAccessInAccessList('PROVIDER_SCHEDULE_EDIT') && id) || (findAccessInAccessList('PROVIDER_SCHEDULE_CREATE') && !id)) &&
                  <button
                    type='submit'
                    className="btn btn-primary rounded d-flex align-items-center me-2 px-4 p-2"
                  >
                    { id ? "ذخیره تغییرات" : "افزودن زمان‌بندی" }
                  </button>
                }
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddSchedule;